import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { BaseButton, Variables } from '../../base';
import { ComposeInput, IComposeInputProps } from './compose-input';
import { ComposeTextarea, IComposeTextareaProps } from './compose-textarea';

export const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: none;
    background-color: ${Variables.ThemeLight.Background2};
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    padding: 14px 16px;
`;

export const HeaderIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${Variables.ThemeLight.PrimaryMild1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

export const Title = styled.div`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const Subtitle = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.regular};
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMild};
`;

export const CloseButton = styled(BaseButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: unset;
    height: auto;
    background: transparent;
    background-color: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    cursor: pointer;
    color: ${Variables.Colors.n50};
    transition: color 0.2s ease;

    &:hover {
        color: ${Variables.Colors.n70};
        background: transparent;
        background-color: transparent;
    }
`;

export const CloseIcon = styled(Icon)``;

interface IBodyProps {
    isNote?: boolean;
}

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const Label = styled.div`
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMild};
    line-height: 1;
`;

export const Body = styled.div<IBodyProps>`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
    padding: 16px 24px 8px;
    overflow-y: auto;
    flex: 1;

    ${(props: IBodyProps) =>
        props.isNote &&
        `
        background: linear-gradient(180deg, ${Variables.ThemeLight.NeutralMild2} 0%, ${Variables.ThemeLight.Background2} 100%);
    `}

    input,
    textarea,
    .select__control {
        border: 1px solid ${Variables.ThemeLight.Border2};
        border-radius: 8px;
        font-size: 14px;
        font-weight: ${Variables.FontWeight.regular};
        color: ${Variables.ThemeLight.TextStrong};
        background-color: ${Variables.ThemeLight.Background3};
        &::placeholder {
            color: ${Variables.ThemeLight.TextMild};
            font-size: 14px;
            font-weight: ${Variables.FontWeight.regular};
        }
    }

    input:hover,
    textarea:hover,
    .select__control:hover {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
    }

    input:focus,
    input:active,
    textarea:focus,
    textarea:active,
    select:focus,
    .select__control--is-focused {
        border: 1px solid ${Variables.ThemeLight.PrimaryConstant6} !important;
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryOpacity3} !important;
        outline: none;
    }
`;

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const RecipientCard = styled.div<{ isPrivate?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border-radius: 10px;
    border: 1px solid
        ${(props: { isPrivate?: boolean }) =>
            props.isPrivate ? Variables.ThemeLight.NeutralMildB1 : Variables.ThemeLight.PrimaryConstant6};
    box-shadow: 0 0 0 3px
        ${(props: { isPrivate?: boolean }) =>
            props.isPrivate ? Variables.ThemeLight.NeutralMild1 : Variables.ThemeLight.PrimaryOpacity3};
    background: ${(props: { isPrivate?: boolean }) =>
        props.isPrivate ? Variables.ThemeLight.NeutralMild3 : Variables.ThemeLight.PrimaryOpacity3};
`;

export const RecipientCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const RecipientCardText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

export const RecipientTitle = styled.span`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.semibold};
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const RecipientTagline = styled.span`
    font-size: 12px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMild};
`;

export const RecipientBlurb = styled.p`
    margin: 0;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const RecipientEta = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const StyledComposeInput = styled(ComposeInput)<IComposeInputProps>``;

export const StyledComposeTextarea = styled(ComposeTextarea)<IComposeTextareaProps>``;

export const TemplateRow = styled.div`
    display: flex;
    gap: 8px;
    max-height: 56px;
`;

export const TemplateColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

export const StyledMessageFields = styled.div`
    & > div {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
`;

export const PrivateNoteHint = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.regular};
    line-height: 140%;
    color: ${Variables.ThemeLight.TextMedium};
    padding: 8px 12px;
    border-radius: 6px;
    background-color: ${Variables.ThemeLight.NeutralMild3};
    border: 1px solid ${Variables.Colors.b300_l90};
`;

export const MessageToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    border-top: 1px solid ${Variables.ThemeLight.Border2};
`;

export const MessageToolbarHint = styled.span`
    margin-left: auto;
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const AttachLabelButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: unset;
    height: auto;
    padding: 6px 8px;
    background: transparent;
    background-color: transparent;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMedium};
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.NeutralMild1};
        background-color: ${Variables.ThemeLight.NeutralMild1};
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

export const Footer = styled.div`
    flex: none;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid ${Variables.ThemeLight.Border2};
    background: ${Variables.ThemeLight.Background2};
`;

export const FooterHint = styled.span`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const FooterActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const FooterToggles = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;
